import React, { useState, useEffect, useRef } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiBell, FiBellOff } = FiIcons;

const LiveNotifier = ({ game }) => {
  const [enabled, setEnabled] = useState(false);
  const [permission, setPermission] = useState(
    'Notification' in window ? Notification.permission : 'denied'
  );
  
  const prevScoreRef = useRef(null);
  const initializedRef = useRef(false);

  const gamePk = game?.gamePk;
  const isLive = game?.status?.abstractGameState === 'Live';
  const awayAbbr = game?.teams?.away?.team?.abbreviation || 'AWY';
  const homeAbbr = game?.teams?.home?.team?.abbreviation || 'HME';

  const awayScore = game?.linescore?.teams?.away?.runs ?? game?.teams?.away?.score ?? 0;
  const homeScore = game?.linescore?.teams?.home?.runs ?? game?.teams?.home?.score ?? 0;

  useEffect(() => {
    if (!isLive || !gamePk) return;

    // Ensure we don't notify on the first mount or page reload
    if (!initializedRef.current) {
      prevScoreRef.current = { away: awayScore, home: homeScore };
      initializedRef.current = true;
      return;
    }

    const prev = prevScoreRef.current;

    // Check if the score has increased securely
    if (enabled && permission === 'granted' && prev) {
      if (awayScore > prev.away || homeScore > prev.home) {
        // Score increased! Fetch the latest playByPlay to get the context (like if it was a Home Run)
        fetch(`https://statsapi.mlb.com/api/v1/game/${gamePk}/playByPlay`)
          .then((r) => r.json())
          .then((data) => {
            const plays = data.allPlays || [];
            // Find the latest play with an RBI or Home Run to describe the scoring event
            const scoringPlay = [...plays].reverse().find(
              (p) => p.result?.rbi > 0 || p.result?.event === 'Home Run'
            ) || plays[plays.length - 1];

            const isHR = scoringPlay?.result?.event === 'Home Run';
            const desc = scoringPlay?.result?.description || 'Score change detected!';
            const title = isHR ? `💣 HOME RUN!` : `⚾ SCORE UPDATE`;
            const body = `${desc}\n${awayAbbr} ${awayScore} - ${homeScore} ${homeAbbr}`;

            if ('Notification' in window) {
              new Notification(title, { body, icon: '/favicon.ico' });
            }
          })
          .catch((err) => {
            // Fallback notification if the playByPlay fetch fails
            if ('Notification' in window) {
              new Notification(`⚾ SCORE UPDATE`, {
                body: `${awayAbbr} ${awayScore} - ${homeScore} ${homeAbbr}`,
                icon: '/favicon.ico'
              });
            }
          });
      }
    }

    prevScoreRef.current = { away: awayScore, home: homeScore };
  }, [awayScore, homeScore, isLive, gamePk, enabled, permission, awayAbbr, homeAbbr]);

  const toggleNotifications = async () => {
    if (!('Notification' in window)) {
      alert('Push notifications are not supported in this browser.');
      return;
    }

    if (!enabled) {
      if (permission !== 'granted') {
        const perm = await Notification.requestPermission();
        setPermission(perm);
        if (perm === 'granted') setEnabled(true);
      } else {
        setEnabled(true);
      }
    } else {
      setEnabled(false);
    }
  };

  if (!isLive) return null;

  return (
    <button
      onClick={toggleNotifications}
      className={`flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 border-2 transition-colors font-bold uppercase tracking-widest text-[9px] sm:text-xs shrink-0 ${
        enabled
          ? 'bg-[#e85555] border-[#e85555] text-white shadow-[0_0_10px_rgba(232,85,85,0.4)]'
          : 'bg-[rgba(0,0,0,0.2)] border-[#658d71] text-[#a9c9b1] hover:border-[#a9c9b1] hover:text-white'
      }`}
      title={enabled ? "Disable Live Score Notifications" : "Enable Live Score Notifications"}
    >
      <SafeIcon icon={enabled ? FiBell : FiBellOff} size={14} />
      <span className="hidden sm:inline">
        {enabled ? 'Alerts On' : 'Alerts Off'}
      </span>
    </button>
  );
};

export default LiveNotifier;